<template>
  <div class="cms-item-versions">
    <drawer-dialog-controls
      :is-loading="isLoading"
      is-pristine
      is-disabled
      @close="$emit('close')"
    >
      Versions for "{{ item.name }}"
    </drawer-dialog-controls>

    <div class="form-content">
      <v-card>
        <v-data-table
          :headers="headers"
          :items="versions"
          :loading="isVersionsLoading"
          item-key="version_id"
          :sort-by="['version_num']"
          :sort-desc="[true]"
        >
          <template #item.actions="{ item: row }">
            <v-btn small text color="primary" :loading="loadingVersionId === row.version_id" @click="onView(row)">
              View
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog v-model="dataDialog" max-width="900px" scrollable>
        <v-card>
          <v-card-title>
            Version #{{ viewVersion && viewVersion.version_num }}
            <v-spacer />
            <v-btn small text @click="viewMode = viewMode === 'form' ? 'json' : 'form'">
              {{ viewMode === 'form' ? 'JSON View' : 'Form View' }}
            </v-btn>
            <v-btn small color="warning" class="ml-2" @click="showRollbackConfirm = true">
              <v-icon small left>mdi-backup-restore</v-icon>
              Rollback
            </v-btn>
            <v-btn icon class="ml-1" @click="dataDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text style="max-height: 70vh; overflow-y: auto;" class="pt-4">
            <template v-if="viewMode === 'form'">
              <!-- System fields -->
              <v-card outlined class="mb-4">
                <v-card-title class="subtitle-2 py-2">Item Settings</v-card-title>
                <v-divider />
                <v-container>
                  <v-row dense>
                    <v-col cols="4">
                      <v-text-field :value="viewFullObject.name" label="Name" dense outlined hide-details readonly />
                    </v-col>
                    <v-col cols="4">
                      <v-text-field :value="viewFullObject.slug" label="Slug" dense outlined hide-details readonly />
                    </v-col>
                    <v-col cols="4">
                      <v-text-field :value="viewFullObject.status" label="Status" dense outlined hide-details readonly />
                    </v-col>
                  </v-row>
                  <v-row v-if="viewFullObject.environments" dense class="mt-2">
                    <v-col cols="6">
                      <v-text-field :value="formatArray(viewFullObject.environments)" label="Environments" dense outlined hide-details readonly />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field :value="formatArray(viewFullObject.category_ids)" label="Categories" dense outlined hide-details readonly />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>

              <!-- Content fields -->
              <v-card v-if="viewSchema && viewSchema.fields && hasContentData" outlined>
                <v-card-title class="subtitle-2 py-2">Content</v-card-title>
                <v-divider />
                <v-container>
                  <schema-field
                    :fields="viewSchema.fields"
                    :fieldsets="viewSchema.fieldsets"
                    :value="viewDataObject"
                    :root-value="viewDataObject"
                    :locales="viewLocales"
                    :entity-id="item.entity_id"
                    readonly
                  />
                </v-container>
              </v-card>
            </template>
            <pre v-else style="font-size: 13px; white-space: pre-wrap;">{{ formattedData }}</pre>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showRollbackConfirm" max-width="420">
        <v-card>
          <v-card-title class="warning--text">Rollback to version #{{ viewVersion && viewVersion.version_num }}?</v-card-title>
          <v-card-text>
            This will overwrite the current item state with data from this version.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="showRollbackConfirm = false">Cancel</v-btn>
            <v-btn color="warning" :loading="rollbackLoading" @click="onRollback">Rollback</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { API } from '@/utils/api'
import { DrawerDialogControls } from '@/components/core/DrawerDialog'
import SchemaField from './item-form/SchemaField.vue'
export default {
  components: {
    DrawerDialogControls,
    SchemaField,
  },
  props: {
    item: Object,
    isLoading: Boolean,
  },
  data() {
    return {
      versions: [],
      isVersionsLoading: false,
      loadingVersionId: null,
      dataDialog: false,
      viewMode: 'form',
      formattedData: '',
      viewFullObject: {},
      viewDataObject: {},
      viewSchema: null,
      viewLocales: [],
      viewVersion: null,
      showRollbackConfirm: false,
      rollbackLoading: false,
      headers: [
        { text: 'Version', value: 'version_num', width: 100 },
        { text: 'Version ID', value: 'version_id', width: 120 },
        { text: 'User ID', value: 'user_id', width: 100 },
        { text: 'Created', value: 'created' },
        { text: '', value: 'actions', sortable: false, width: 100 },
      ],
    }
  },
  computed: {
    hasContentData() {
      return this.viewDataObject && typeof this.viewDataObject === 'object' && Object.keys(this.viewDataObject).length > 0
    },
  },
  mounted() {
    this.loadVersions()
  },
  methods: {
    loadVersions() {
      this.isVersionsLoading = true
      API.call('/cms/version/list.json', {
        item_id: this.item.item_id,
        entity_id: this.item.entity_id,
      })
        .then(r => {
          this.versions = r.data.items || []
        })
        .finally(() => this.isVersionsLoading = false)
    },
    onView(row) {
      this.loadingVersionId = row.version_id
      API.call('/cms/version/get.json', {
        version_id: row.version_id,
        type_id: this.item.type_id,
        entity_id: this.item.entity_id,
      }).then(r => {
        const resp = r.data || {}
        const versionItem = resp.item || resp

        // versionItem.data is the full item snapshot
        let fullObj = versionItem.data || {}
        if (typeof fullObj === 'string') {
          try {
            fullObj = JSON.parse(fullObj)
          } catch (e) {
            fullObj = {}
          }
        }

        // Extract content data (nested data field)
        let contentData = fullObj.data || {}
        if (typeof contentData === 'string') {
          try {
            contentData = JSON.parse(contentData)
          } catch (e) {
            contentData = {}
          }
        }

        this.viewVersion = row
        this.viewFullObject = fullObj
        this.viewDataObject = contentData
        this.viewSchema = resp.schema || null
        this.viewLocales = resp.locales || []

        try {
          this.formattedData = JSON.stringify(fullObj, null, 2)
        } catch (e) {
          this.formattedData = String(versionItem.data)
        }

        this.viewMode = 'form'
        this.dataDialog = true
      }).catch(() => {
        this.viewMode = 'json'
        this.formattedData = 'Failed to load version'
        this.dataDialog = true
      }).finally(() => {
        this.loadingVersionId = null
      })
    },
    onRollback() {
      this.rollbackLoading = true
      const rollbackData = { ...this.viewFullObject }
      // Ensure item_id and entity_id are set from the current item
      rollbackData.item_id = this.item.item_id
      rollbackData.entity_id = this.item.entity_id
      // Stringify data if it's an object
      if (rollbackData.data && typeof rollbackData.data === 'object') {
        rollbackData.data = JSON.stringify(rollbackData.data)
      }
      API.call('/cms/item/upsert.json', rollbackData).then(() => {
        this.showRollbackConfirm = false
        this.dataDialog = false
        this.$emit('rollback')
      }).catch(() => {
        // error
      }).finally(() => {
        this.rollbackLoading = false
      })
    },
    formatArray(val) {
      if (!Array.isArray(val) || !val.length) {
        return '—'
      }
      return val.join(', ')
    },
  },
}
</script>

<style scoped>
.form-content {
  background-color: #F2F2F2;
  padding: 1rem;
}
</style>
